import { useState, useEffect } from 'react';
import { guildsAPI } from '../../api';

const emptyRank = { name: '', level: 1, hourly_rate: 0, required_hours: 0 };

// RankForm defined OUTSIDE RanksPage to prevent remount on every keystroke
function RankForm({ formData, setFormData, onSave, onCancel, saving, sym }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {[
        { label: 'Rank Name', key: 'name', type: 'text', placeholder: 'e.g. Recruit, Officer, Commander' },
        { label: 'Level (1 = lowest)', key: 'level', type: 'number', placeholder: '1' },
        { label: `Hourly Rate (${sym})`, key: 'hourly_rate', type: 'number', placeholder: '10.00' },
        { label: 'Required Hours (0 = none)', key: 'required_hours', type: 'number', placeholder: '0' },
      ].map(field => (
        <div key={field.key}>
          <label style={{
            fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)',
            display: 'block', marginBottom: '6px'
          }}>
            {field.label}
          </label>
          <input
            type={field.type}
            placeholder={field.placeholder}
            value={formData[field.key]}
            onChange={e => setFormData(prev => ({
              ...prev,
              [field.key]: field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value
            }))}
            style={{
              width: '100%', padding: '10px 12px', boxSizing: 'border-box',
              border: '2px solid var(--border)', borderRadius: '10px',
              fontSize: '14px', fontFamily: 'var(--font-body)',
              background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'}
          />
        </div>
      ))}
      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={onSave} disabled={saving} style={{
          flex: 1, padding: '12px',
          background: saving ? 'var(--text-muted)' : 'var(--gradient-accent)',
          color: '#fff', border: 'none', borderRadius: '10px',
          fontSize: '14px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
          fontFamily: 'var(--font-display)',
        }}>
          {saving ? 'Saving...' : 'Save Rank'}
        </button>
        <button onClick={onCancel} style={{
          padding: '12px 20px', background: 'var(--bg-secondary)',
          border: '1px solid var(--border)', borderRadius: '10px',
          fontSize: '14px', cursor: 'pointer', fontFamily: 'var(--font-body)',
          color: 'var(--text-secondary)',
        }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function RanksPage({ guild }) {
  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState(emptyRank);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const sym = guild?.currency_symbol || '$';

  useEffect(() => {
    guildsAPI.getRanks(guild.id)
      .then(res => setRanks(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [guild.id]);

  const handleCreate = async () => {
    if (!formData.name.trim()) return alert('Please enter a rank name.');
    setSaving(true);
    try {
      const res = await guildsAPI.createRank(guild.id, formData);
      setRanks(prev => [...prev, res.data].sort((a, b) => a.level - b.level));
      setCreating(false);
      setFormData(emptyRank);
    } catch { alert('Failed to create rank.'); }
    finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) return alert('Please enter a rank name.');
    setSaving(true);
    try {
      const res = await guildsAPI.updateRank(guild.id, editing.id, formData);
      setRanks(prev => prev.map(r => r.id === editing.id ? { ...r, ...res.data } : r));
      setEditing(null);
    } catch { alert('Failed to update rank.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (rankId) => {
    if (!window.confirm('Delete this rank? Staff with this rank will be set to no rank.')) return;
    setDeleting(rankId);
    try {
      await guildsAPI.deleteRank(guild.id, rankId);
      setRanks(prev => prev.filter(r => r.id !== rankId));
      if (editing?.id === rankId) setEditing(null);
    } catch { alert('Failed to delete rank.'); }
    finally { setDeleting(null); }
  };

  const openEdit = (rank) => {
    setEditing(rank);
    setCreating(false);
    setFormData({ name: rank.name, level: rank.level, hourly_rate: rank.hourly_rate, required_hours: rank.required_hours || 0 });
  };

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    const nextLevel = ranks.length > 0 ? Math.max(...ranks.map(r => r.level)) + 1 : 1;
    setFormData({ ...emptyRank, level: nextLevel });
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>Loading ranks...
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: (editing || creating) ? '1fr 360px' : '1fr', gap: '24px' }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Ranks</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>{ranks.length} rank{ranks.length !== 1 ? 's' : ''} configured</p>
          </div>
          <button onClick={openCreate} style={{
            padding: '12px 24px', background: 'var(--gradient-accent)',
            color: '#fff', border: 'none', borderRadius: '12px',
            fontSize: '14px', fontWeight: 700, cursor: 'pointer',
            fontFamily: 'var(--font-display)', boxShadow: 'var(--shadow)',
          }}>+ New Rank</button>
        </div>

        {ranks.length === 0 ? (
          <div style={{
            background: 'var(--bg-card)', border: '2px dashed var(--border)',
            borderRadius: '16px', padding: '60px', textAlign: 'center',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏷️</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>No ranks yet</div>
            <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '20px' }}>
              Create ranks to assign pay rates to your staff members.
            </div>
            <button onClick={openCreate} style={{
              padding: '12px 24px', background: 'var(--gradient-accent)',
              color: '#fff', border: 'none', borderRadius: '12px',
              fontSize: '14px', fontWeight: 700, cursor: 'pointer',
              fontFamily: 'var(--font-display)',
            }}>Create First Rank</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {ranks.map(rank => (
              <div key={rank.id} style={{
                background: 'var(--bg-card)',
                border: `2px solid ${editing?.id === rank.id ? 'var(--accent-primary)' : 'var(--border)'}`,
                borderRadius: '16px', padding: '20px 24px',
                display: 'flex', alignItems: 'center', gap: '20px',
                boxShadow: 'var(--shadow-card)', transition: 'border-color 0.2s',
              }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '12px',
                  background: 'var(--gradient-accent)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '20px', fontWeight: 900, color: '#fff',
                  fontFamily: 'var(--font-display)', flexShrink: 0,
                }}>{rank.level}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
                    {rank.name}
                  </div>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    <span>💰 {sym}{parseFloat(rank.hourly_rate).toFixed(2)}/hr</span>
                    <span>⏱️ {parseFloat(rank.required_hours) > 0 ? `${rank.required_hours}h required` : 'No hour req.'}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => openEdit(rank)} style={{
                    padding: '8px 16px', borderRadius: '8px', cursor: 'pointer',
                    border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                    fontSize: '13px', color: 'var(--text-secondary)', fontFamily: 'var(--font-body)',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.color = 'var(--accent-primary)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
                    ✏️ Edit
                  </button>
                  <button onClick={() => handleDelete(rank.id)} disabled={deleting === rank.id} style={{
                    padding: '8px 16px', borderRadius: '8px', cursor: 'pointer',
                    border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                    fontSize: '13px', color: 'var(--danger)', fontFamily: 'var(--font-body)',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--danger)'; e.currentTarget.style.background = '#FFF0F0'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-secondary)'; }}>
                    {deleting === rank.id ? '...' : '🗑️ Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(editing || creating) && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '16px', padding: '24px', height: 'fit-content',
          boxShadow: 'var(--shadow-card)', position: 'sticky', top: '80px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)' }}>
              {editing ? `Edit: ${editing.name}` : 'Create New Rank'}
            </h2>
            <button onClick={() => { setEditing(null); setCreating(false); }} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '18px', color: 'var(--text-muted)',
            }}>✕</button>
          </div>
          <RankForm
            formData={formData}
            setFormData={setFormData}
            onSave={editing ? handleUpdate : handleCreate}
            onCancel={() => { setEditing(null); setCreating(false); }}
            saving={saving}
            sym={sym}
          />
        </div>
      )}
    </div>
  );
}
